import { useEffect } from "react";

/**
 * Gestisce title + meta tags dinamici per SEO, social sharing (OG/Twitter) e JSON-LD.
 * Esegue mutazioni idempotenti sul <head>: ogni attributo e' rinfrescato al mount
 * e ripulito all'unmount riportando i valori originali dell'index.html.
 *
 * Props:
 *  - title: string (obbligatorio)
 *  - description: string (obbligatorio)
 *  - canonical: string (path, concatenato al dominio)
 *  - ogImage: string (URL assoluto, default og-image.png)
 *  - ogType: "website" | "article" | "profile" (default "website")
 *  - keywords: string[] (opzionali, taggati su meta keywords)
 *  - noindex: boolean (true → robots noindex per pagine private/tecniche)
 *  - jsonLd: object | object[] (Schema.org, si accettano piu' schemi)
 *  - articleMeta: { publishedTime, modifiedTime, author, section } per OG article
 */
export default function SEOHead({
  title,
  description,
  canonical,
  ogImage = "https://www.colucciassicuratori.com/og-image.png",
  ogType = "website",
  keywords = null,
  noindex = false,
  jsonLd = null,
  articleMeta = null,
}) {
  useEffect(() => {
    const baseDomain = "https://www.colucciassicuratori.com";
    const fullCanonical = canonical
      ? `${baseDomain}${canonical.startsWith("/") ? canonical : "/" + canonical}`
      : baseDomain;

    const prev = { title: document.title };
    if (title) document.title = title;

    const setMeta = (selector, attr, value) => {
      if (value === null || value === undefined || value === "") return null;
      let el = document.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        const [k, v] = selector.match(/\[(.+?)="(.+?)"\]/).slice(1);
        el.setAttribute(k, v);
        document.head.appendChild(el);
      }
      const prevValue = el.getAttribute(attr);
      el.setAttribute(attr, value);
      return { el, attr, prevValue, created };
    };

    const setLink = (rel, href, extra = {}) => {
      if (!href) return null;
      let el = document.querySelector(`link[rel="${rel}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      const prevHref = el.getAttribute("href");
      el.setAttribute("href", href);
      return { el, attr: "href", prevValue: prevHref, created };
    };

    const touched = [];

    // Primary meta
    touched.push(setMeta('meta[name="description"]', "content", description));
    if (keywords && keywords.length) {
      touched.push(setMeta('meta[name="keywords"]', "content", keywords.join(", ")));
    }
    touched.push(
      setMeta('meta[name="robots"]', "content", noindex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1")
    );
    touched.push(setMeta('meta[name="googlebot"]', "content", noindex ? "noindex, nofollow" : "index, follow"));

    // OpenGraph
    touched.push(setMeta('meta[property="og:title"]', "content", title));
    touched.push(setMeta('meta[property="og:description"]', "content", description));
    touched.push(setMeta('meta[property="og:url"]', "content", fullCanonical));
    touched.push(setMeta('meta[property="og:image"]', "content", ogImage));
    touched.push(setMeta('meta[property="og:image:width"]', "content", "1200"));
    touched.push(setMeta('meta[property="og:image:height"]', "content", "630"));
    touched.push(setMeta('meta[property="og:type"]', "content", ogType));
    touched.push(setMeta('meta[property="og:locale"]', "content", "it_IT"));
    touched.push(setMeta('meta[property="og:site_name"]', "content", "Colucci Assicuratori"));

    // Article-specific (blog post)
    if (articleMeta) {
      if (articleMeta.publishedTime)
        touched.push(setMeta('meta[property="article:published_time"]', "content", articleMeta.publishedTime));
      if (articleMeta.modifiedTime)
        touched.push(setMeta('meta[property="article:modified_time"]', "content", articleMeta.modifiedTime));
      if (articleMeta.author)
        touched.push(setMeta('meta[property="article:author"]', "content", articleMeta.author));
      if (articleMeta.section)
        touched.push(setMeta('meta[property="article:section"]', "content", articleMeta.section));
    }

    // Twitter
    touched.push(setMeta('meta[name="twitter:card"]', "content", "summary_large_image"));
    touched.push(setMeta('meta[name="twitter:title"]', "content", title));
    touched.push(setMeta('meta[name="twitter:description"]', "content", description));
    touched.push(setMeta('meta[name="twitter:image"]', "content", ogImage));
    touched.push(setMeta('meta[name="twitter:site"]', "content", "@ColucciAssic"));

    // Canonical
    touched.push(setLink("canonical", fullCanonical));

    // Alternate language (IT only for now)
    touched.push(setMeta('meta[http-equiv="content-language"]', "content", "it-IT"));

    // JSON-LD (supporta array o singolo oggetto)
    const ldScripts = [];
    if (jsonLd) {
      const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      schemas.forEach((schema) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.dataset.seoHead = "dynamic";
        s.textContent = JSON.stringify(schema);
        document.head.appendChild(s);
        ldScripts.push(s);
      });
    }

    return () => {
      document.title = prev.title;
      touched.forEach((t) => {
        if (!t) return;
        if (t.created) {
          t.el.remove();
        } else if (t.prevValue !== null) {
          t.el.setAttribute(t.attr, t.prevValue);
        }
      });
      ldScripts.forEach((s) => s.parentNode && s.remove());
    };
  }, [title, description, canonical, ogImage, ogType, keywords, noindex, jsonLd, articleMeta]);

  return null;
}
